import React, { useState, useEffect } from 'react';
import { clientApi } from '../lib/clientAuth';
import { Building2, Users } from 'lucide-react';

export default function ClientOrganisationPage() {
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchDepartments();
    fetchEmployees();
  }, []);

  const fetchDepartments = async () => {
    try {
      const r = await clientApi.get('/api/departments');
      setDepartments(r.data || []);
    } catch (e) {
      console.error('Error fetching departments:', e);
    }
  };

  const fetchEmployees = async () => {
    try {
      const r = await clientApi.get('/api/client/team');
      setEmployees(r.data || []);
    } catch (e) {
      console.error('Error fetching team:', e);
    }
  };

  const getDepartmentEmployees = (dept) => {
    // Match by department name since employees store department names, not IDs
    return employees.filter(emp => 
      emp.departments?.includes(dept.name) || 
      emp.employment?.departments?.includes(dept.name)
    );
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1a1a2e]">Organisation</h1>
        <p className="text-neutral-600 mt-1">View agency departments and team structure</p>
      </div>

      {/* Tabs - Only Departments for Client */}
      <div className="flex items-center gap-2 mb-6 border-b border-white/40">
        <button className="px-6 py-3 font-medium border-b-2 border-blue-600 text-blue-600 flex items-center gap-2">
          <Building2 className="w-4 h-4" /> Departments
        </button>
      </div>

      {/* Departments Grid (View Only) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {departments.length === 0 ? (
          <div className="col-span-full glass-card p-12 text-center text-neutral-500">
            No departments available.
          </div>
        ) : (
          departments.map(dept => {
            const deptEmployees = getDepartmentEmployees(dept);
            return (
              <div key={dept._id} className="soft-card overflow-hidden">
                <div className="p-6 border-b border-white/30 bg-gradient-to-r from-blue-50 to-blue-100/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-[#1a1a2e]">{dept.name}</h3>
                      <p className="text-sm text-neutral-500">{deptEmployees.length} members</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  {dept.description && (
                    <p className="text-sm text-neutral-600 mb-4">{dept.description}</p>
                  )}
                  <p className="text-sm font-medium text-neutral-700 mb-3">Team Members</p>
                  <div className="space-y-2">
                    {deptEmployees.slice(0, 5).map(emp => (
                      <div key={emp._id} className="flex items-center gap-3 text-sm">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium text-xs">{(emp.personal?.full_name || emp.work_email)?.[0]?.toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="font-medium text-[#1a1a2e]">{emp.personal?.full_name || emp.work_email}</p>
                          <p className="text-xs text-neutral-500">{emp.designation}</p>
                        </div>
                      </div>
                    ))}
                    {deptEmployees.length > 5 && (
                      <p className="text-sm text-neutral-500 pl-11">+{deptEmployees.length - 5} more</p>
                    )}
                    {deptEmployees.length === 0 && (
                      <p className="text-sm text-neutral-400">No team members assigned</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-[#1a1a2e]">About Our Team</h3>
            <p className="text-sm text-neutral-600 mt-1">
              Our agency is organized into specialized departments to serve you better. Each department has dedicated professionals who work on different aspects of your projects. If you need to connect with a specific team, please reach out through the Help Desk.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
